import { ChevronDown, FolderKanban, Check, Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { colorFor, initials } from "@/components/ui/avatar-initials";
import { useProjects } from "@/hooks/useProjects";
import { useTranslation } from "react-i18next";

interface ProjectSelectorProps {
  orgSlug: string;
  activeProjectSlug: string | null;
}

export function ProjectSelector({ orgSlug, activeProjectSlug }: ProjectSelectorProps) {
  const { t } = useTranslation();
  const { projects, loading } = useProjects();
  const navigate = useNavigate();

  const activeProject = projects.find((p) => p.slug === activeProjectSlug) ?? null;

  const handleSelectProject = (slug: string) => {
    navigate(`/${orgSlug}/projects/${slug}/overview`);
  };

  const handleSelectAll = () => {
    navigate(`/${orgSlug}/`);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        disabled={loading}
        className="flex w-full items-center gap-2.5 rounded-xl bg-sidebar-foreground/5 px-2 py-2 text-left ring-1 ring-sidebar-border/40 transition-all hover:bg-sidebar-foreground/10 hover:ring-sidebar-border/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring"
        aria-label={activeProject?.name ?? t("nav.allProjects", "All projects")}
      >
        {activeProject ? (
          <div
            className={`flex h-6 w-6 shrink-0 items-center justify-center rounded text-[9px] font-bold ${colorFor(activeProject.name)}`}
          >
            {initials(activeProject.name)}
          </div>
        ) : (
          <FolderKanban className="h-5 w-5 shrink-0 text-muted-foreground" />
        )}
        <span className="min-w-0 flex-1 truncate text-sm font-medium text-sidebar-foreground">
          {activeProject?.name ?? t("nav.allProjects", "All projects")}
        </span>
        <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform duration-200 [[data-state=open]>&]:rotate-180" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56 max-h-72 overflow-y-auto">
        <DropdownMenuItem
          onSelect={handleSelectAll}
          className="flex items-center gap-2 px-3 py-2"
        >
          <FolderKanban className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="flex-1 text-sm">{t("nav.allProjects", "All projects")}</span>
          {!activeProject && <Check className="h-3.5 w-3.5 text-primary" />}
        </DropdownMenuItem>
        {loading && (
          <>
            <DropdownMenuSeparator />
            {[1, 2, 3].map((i) => (
              <DropdownMenuItem key={i} disabled className="flex items-center gap-2 px-3 py-2">
                <div className="h-5 w-5 shrink-0 rounded bg-muted animate-pulse" />
                <div className="h-4 flex-1 rounded bg-muted animate-pulse" />
              </DropdownMenuItem>
            ))}
          </>
        )}
        {!loading && projects.length > 0 && <DropdownMenuSeparator />}
        {!loading && projects.map((p) => (
          <DropdownMenuItem
            key={p.id}
            onSelect={() => handleSelectProject(p.slug)}
            className="flex items-center gap-2 px-3 py-2"
          >
            <div
              className={`flex h-5 w-5 shrink-0 items-center justify-center rounded text-[9px] font-bold ${colorFor(p.name)}`}
            >
              {initials(p.name)}
            </div>
            <span className="flex-1 truncate text-sm">{p.name}</span>
            {activeProjectSlug === p.slug && <Check className="h-3.5 w-3.5 text-primary" />}
          </DropdownMenuItem>
        ))}
        {!loading && projects.length === 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem disabled className="justify-center px-3 py-2 text-xs text-muted-foreground">
              {t("nav.noProjects", "No projects yet")}
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={() => navigate(`/${orgSlug}/projects`)}
              className="flex items-center gap-1 justify-center px-3 py-2 text-xs text-primary"
            >
              <Plus className="h-3 w-3" />
              {t("nav.createProject", "Create project")}
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
